import { Alert, Box, Button, CloseButton, Flex, Text } from '@chakra-ui/react';
import { ReactComponent as Icon } from 'assets/icons/design_ai_gradient.svg';
import { getAutoBrand, updateBrandFonts } from 'modules/Editor/components/AutoBrand/AutoBrand.helpers';
import { BrandState } from 'modules/User';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Loading } from '../BrandKitTab/Loading';

export type SuggestionsFontProps = {
  brandData?: BrandState;
  refreshBrandData?: () => void;
};

export const SuggestionsFont = memo((props: SuggestionsFontProps) => {
  const { t } = useTranslation('editor_widget_menu', {
    useSuspense: false,
    keyPrefix: 'sideMenu.brandKit.suggestions',
  });
  const [isOpen, setIsOpen] = useState(true);
  const [selectedFont, setSelectedFont] = useState<string>('');

  const getBrandFonts = useMemo(
    () => async () => {
      try {
        const userBrandUrl = props.brandData?.metadata?.site_url || '';
        const data = await getAutoBrand(userBrandUrl, true);
        const suggestedFonts = data?.suggestedFonts || 'Poppins'; // fallback font
        setSelectedFont(suggestedFonts);
      } catch (error) {
        console.log('error', error);
      }
    },
    [props.brandData],
  );

  useEffect(() => {
    if (!isOpen) return;
    getBrandFonts();
  }, [isOpen, getBrandFonts]);

  if (!isOpen) {
    return null;
  }

  const onClose = () => setIsOpen(false);

  const onUpdateFont = async () => {
    try {
      if (!props.brandData) return;
      const updateFontResponse = await updateBrandFonts(props.brandData, selectedFont);
      if (updateFontResponse.success) {
        onClose();
        props?.refreshBrandData?.();
      }
    } catch (error) {
      // noop
    }
  };

  return (
    <Box mt='2rem'>
      {selectedFont ? (
        <>
          <Flex alignItems='center'>
            <Text as='span' fontSize='xs' fontWeight='medium' mr='4px'>
              {t('title')}
            </Text>
            <Icon width='15px' height='15px' />
          </Flex>
          <Alert bg='violet.50' borderRadius='4px' p='13px' display='flex' flexDir='column' mt='1rem'>
            <CloseButton
              alignSelf='flex-start'
              position='absolute'
              right={1}
              top={1}
              onClick={onClose}
              color='violet.700'
              _hover={{ bg: 'none' }}
            />
            <Flex mb='10px' gap='13px' mr='auto' alignItems='center'>
              <Flex bg='white' minW='110px' h='57px' justifyContent='center' alignItems='center' borderRadius='4px'>
                <Text as='h1' fontWeight='bold' fontSize='18px' fontFamily={selectedFont}>
                  {selectedFont}
                </Text>
              </Flex>
              <Text as='span' fontSize='xs' fontWeight='medium' color='violet.700' maxW='126px'>
                {t('desc')}
              </Text>
            </Flex>
            <Button
              h='auto'
              p='8px 8px'
              fontSize='xs'
              fontWeight='medium'
              bg='violet.500'
              color='white'
              ml='auto'
              borderRadius='4px'
              _hover={{ bg: 'violet.700' }}
              onClick={onUpdateFont}
            >
              {t('cta')}
            </Button>
          </Alert>
        </>
      ) : (
        <Loading />
      )}
    </Box>
  );
});

SuggestionsFont.displayName = 'SuggestionsFont';
